import * as THREE from 'three';
import { POSTER_LIMITS, posterLayout, type LessonObject } from '@vrlearning/lesson-schema';
import { fitText } from '../engine/text.js';

/**
 * A framed photo, `poster:photo`
 * (docs/superpowers/specs/2026-09-28-posters-design.md).
 *
 * The photo is a plane in a 3 cm charcoal frame on a backing board, with a
 * pale strip under it for the caption and the credit. Sizes come from
 * lesson-schema's `posterLayout`, which framing measures too, so the poster
 * the pupil is placed in front of is the one drawn here.
 *
 * The photo is unlit (`MeshBasicMaterial`, not tone mapped): a photograph
 * already has its light, and the studio's ACES tone mapping would dull it.
 * A lesson never waits for a photo: the frame is built at once with a pale
 * mat, and the photo goes in when it arrives.
 */

/** docs/04: canvas textures at no less than 1024 px per metre. */
export const POSTER_PX_PER_METRE = 1024;

/** What a pupil (or an admin previewing) reads on a poster without its photo. */
export const POSTER_TEXT = {
  unavailable: 'Foto niet beschikbaar',
} as const;

const COLOURS = {
  frame: '#2b2b2e',
  mat: '#ebe7df',
  caption: '#1f1f22',
  credit: '#55555c',
  asked: '#6a6a70',
} as const;

export type PaintLine = { text: string; size: number; colour: string; bold?: boolean };
/** A canvas to paint: its size in pixels, a background and lines of text, centred. */
export type PaintRequest = {
  width: number;
  height: number;
  background: string;
  lines: PaintLine[];
};
export type Painter = (request: PaintRequest) => THREE.Texture;

export type PosterState = 'asked' | 'loading' | 'shown' | 'unavailable';

export type PosterController = {
  readonly photo: THREE.Mesh;
  readonly state: PosterState;
  /** The decoded photo: on it goes, and the poster takes its shape if the lesson gave none. */
  setPhoto(image: ImageBitmap): void;
  unavailable(): void;
  dispose(): void;
};

export type BuiltPoster = {
  /** The backing board: pickable, and lit by a highlight together with the frame. */
  body: THREE.Mesh;
  extras: THREE.Object3D[];
  labelHeight: number;
  poster: PosterController;
};

const px = (metres: number): number => Math.max(1, Math.ceil(metres * POSTER_PX_PER_METRE));

/** Canvas painting, for the browser. In Node (tests) there is no canvas: a blank texture. */
export const paintOnCanvas: Painter = (request) => {
  if (typeof document === 'undefined') return new THREE.Texture();
  const canvas = document.createElement('canvas');
  canvas.width = request.width;
  canvas.height = request.height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = request.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const measure = (text: string, size: number, bold: boolean): number => {
    ctx.font = `${bold ? 'bold ' : ''}${size}px system-ui, sans-serif`;
    return ctx.measureText(text).width;
  };
  const maxWidth = canvas.width * 0.94;
  // Each line fitted on its own (docs/04: text auto-shrinks), then the block centred.
  const fitted = request.lines.map((line) => ({
    line,
    fit: fitText(
      line.text,
      {
        maxWidth,
        maxHeight: (canvas.height * line.size) / totalSize(request.lines),
        fontSize: line.size,
        minFontSize: Math.max(10, Math.round(line.size * 0.5)),
        lineHeight: 1.2,
        bold: line.bold ?? false,
      },
      measure,
    ),
  }));
  const height = fitted.reduce((sum, { fit }) => sum + fit.lines.length * fit.fontSize * 1.2, 0);
  let y = (canvas.height - height) / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (const { line, fit } of fitted) {
    ctx.fillStyle = line.colour;
    ctx.font = `${line.bold ? 'bold ' : ''}${fit.fontSize}px system-ui, sans-serif`;
    for (const text of fit.lines) {
      ctx.fillText(text, canvas.width / 2, y + fit.fontSize * 0.1);
      y += fit.fontSize * 1.2;
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

const totalSize = (lines: readonly PaintLine[]): number =>
  lines.reduce((sum, line) => sum + line.size * 1.2, 0) || 1;

const unitBox = new THREE.BoxGeometry(1, 1, 1);
const unitPlane = new THREE.PlaneGeometry(1, 1);

export function buildPoster(object: LessonObject, options: { paint?: Painter } = {}): BuiltPoster {
  const paint = options.paint ?? paintOnCanvas;
  const depth = POSTER_LIMITS.depth;
  const frameMaterial = new THREE.MeshStandardMaterial({ color: COLOURS.frame, roughness: 0.6 });

  // Unit geometries, sized by scale: a photo with another shape than
  // expected re-lays the poster out without building anything again.
  const board = new THREE.Mesh(unitBox.clone(), frameMaterial);
  board.name = 'poster-board';
  const frameBar = (): THREE.Mesh => {
    const frame = new THREE.Mesh(unitBox.clone(), frameMaterial);
    frame.name = 'poster-frame';
    return frame;
  };
  const frames = [frameBar(), frameBar(), frameBar(), frameBar()] as const;
  const photoMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
  const photo = new THREE.Mesh(unitPlane.clone(), photoMaterial);
  photo.name = 'poster-photo';
  const stripMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
  const strip = new THREE.Mesh(unitPlane.clone(), stripMaterial);
  strip.name = 'poster-strip';

  let state: PosterState = object.media === undefined ? 'asked' : 'loading';
  let photoTexture: THREE.Texture | undefined;
  let photoImage: ImageBitmap | undefined;
  let placeholder: THREE.Texture | undefined;
  // A photo can arrive after the lesson was closed: then it only goes.
  let disposed = false;

  const layOut = (photoAspect?: number): ReturnType<typeof posterLayout> => {
    const layout = posterLayout(object, photoAspect);
    const { outer, frame } = layout;
    board.scale.set(outer.width, outer.height, depth / 2);
    board.position.set(0, 0, -depth / 4);
    const [top, bottom, left, right] = frames;
    top.scale.set(outer.width, frame, depth);
    top.position.set(0, outer.height / 2 - frame / 2, 0);
    bottom.scale.set(outer.width, frame, depth);
    bottom.position.set(0, -outer.height / 2 + frame / 2, 0);
    left.scale.set(frame, outer.height - 2 * frame, depth);
    left.position.set(-outer.width / 2 + frame / 2, 0, 0);
    right.scale.set(frame, outer.height - 2 * frame, depth);
    right.position.set(outer.width / 2 - frame / 2, 0, 0);
    photo.scale.set(layout.photo.width, layout.photo.height, 1);
    photo.position.set(0, layout.photoY, 0.002);
    strip.visible = layout.strip > 0;
    strip.scale.set(layout.photo.width, Math.max(layout.strip, 1e-3), 1);
    strip.position.set(0, layout.stripY, 0.002);
    return layout;
  };

  const paintStrip = (layout: ReturnType<typeof posterLayout>): void => {
    stripMaterial.map?.dispose();
    stripMaterial.map = null;
    if (layout.strip <= 0) return;
    const lines: PaintLine[] = [];
    if (object.caption) {
      lines.push({
        text: object.caption,
        size: layout.captionLetter * POSTER_PX_PER_METRE,
        colour: COLOURS.caption,
        bold: true,
      });
    }
    if (object.credit) {
      lines.push({
        text: object.credit,
        size: layout.creditLetter * POSTER_PX_PER_METRE,
        colour: COLOURS.credit,
      });
    }
    stripMaterial.map = paint({
      width: px(layout.photo.width),
      height: px(layout.strip),
      background: COLOURS.mat,
      lines,
    });
    stripMaterial.needsUpdate = true;
  };

  /** The pale mat in the photo's place: what it asks for, or that it is not there. */
  const paintPlaceholder = (layout: ReturnType<typeof posterLayout>): void => {
    placeholder?.dispose();
    const text =
      state === 'asked'
        ? (object.want ?? '')
        : state === 'unavailable'
          ? POSTER_TEXT.unavailable
          : '';
    const letter = layout.captionLetter * POSTER_PX_PER_METRE;
    // Capped so a 3 m poster's placeholder is not a 3000-pixel canvas.
    const scale = Math.min(1, 2048 / px(Math.max(layout.photo.width, layout.photo.height)));
    placeholder = paint({
      width: Math.round(px(layout.photo.width) * scale),
      height: Math.round(px(layout.photo.height) * scale),
      background: COLOURS.mat,
      lines: text ? [{ text, size: letter * scale, colour: COLOURS.asked }] : [],
    });
    photoMaterial.map = placeholder;
    photoMaterial.needsUpdate = true;
  };

  let layout = layOut();
  paintStrip(layout);
  paintPlaceholder(layout);

  const poster: PosterController = {
    photo,
    get state() {
      return state;
    },
    setPhoto(image) {
      if (disposed) {
        image.close();
        return;
      }
      if (object.aspect === undefined && image.width > 0 && image.height > 0) {
        layout = layOut(image.width / image.height);
        paintStrip(layout);
      }
      photoTexture?.dispose();
      // The decoded bitmap holds the photo's memory until it is closed.
      photoImage?.close();
      photoImage = image;
      const texture = new THREE.Texture(image);
      texture.colorSpace = THREE.SRGBColorSpace;
      // Decoded upright already (loadPosterPhoto): an ImageBitmap ignores flipY.
      texture.flipY = false;
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = 4;
      texture.needsUpdate = true;
      photoTexture = texture;
      placeholder?.dispose();
      placeholder = undefined;
      photoMaterial.map = texture;
      photoMaterial.needsUpdate = true;
      state = 'shown';
    },
    unavailable() {
      if (disposed || state === 'shown') return;
      state = 'unavailable';
      paintPlaceholder(layout);
    },
    dispose() {
      disposed = true;
      photoTexture?.dispose();
      photoImage?.close();
      photoImage = undefined;
      placeholder?.dispose();
      stripMaterial.map?.dispose();
    },
  };

  return {
    body: board,
    extras: [...frames, photo, strip],
    labelHeight: layout.outer.height / 2 + 0.2,
    poster,
  };
}

export type PhotoLoading = {
  fetchImpl?: (url: string) => Promise<Response>;
  decode?: (blob: Blob, options: ImageBitmapOptions) => Promise<ImageBitmap>;
};

/**
 * Fetches a poster's signed URL and puts the photo on. Through the service
 * worker, which keeps it by id for a classroom without wifi (vite.config.ts).
 * Never throws: a photo that cannot be had says so on the poster.
 */
export async function loadPosterPhoto(
  poster: PosterController,
  url: string,
  options: PhotoLoading = {},
): Promise<boolean> {
  const fetchImpl = options.fetchImpl ?? ((u: string) => fetch(u));
  const decode = options.decode ?? ((blob, o) => createImageBitmap(blob, o));
  try {
    const response = await fetchImpl(url);
    if (!response.ok) throw new Error(`photo ${response.status}`);
    // Flipped while decoding: WebGL ignores flipY for an ImageBitmap.
    const image = await decode(await response.blob(), {
      imageOrientation: 'flipY',
      premultiplyAlpha: 'none',
    });
    poster.setPhoto(image);
    return true;
  } catch {
    poster.unavailable();
    return false;
  }
}

/** The desktop preview's corner note when photos did not load: "2 foto's niet geladen". */
export function photoNote(failed: number): string | undefined {
  if (failed <= 0) return undefined;
  return failed === 1 ? '1 foto niet geladen' : `${failed} foto's niet geladen`;
}
